// src/pages/profile/ProfilePage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiUser, FiMail, FiPhone, FiMapPin, FiKey, FiCopy, FiCheckCircle, FiAward } from "react-icons/fi";
import { getProfile } from "../../api/userApi";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/common/Button";
import { upgradeAccount } from "../../api/userApi";
import { getWalletOverview } from "../../api/walletApi";
import { STATUS_LEVEL } from "../../utils/status";

function ProfilePage() {
  const { user: authUser, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [loading, setLoading] = useState(true);
  //const [walletLoading, setWalletLoading] = useState(true);

  const [error, setError] = useState("");
  const [upgradeError, setUpgradeError] = useState("");
  const [upgradeSuccess, setUpgradeSuccess] = useState("");
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const userId = authUser?.user_id;

    const [textToCopy, setTextToCopy] = useState('This text will be copied!');
  const [copySuccess, setCopySuccess] = useState('');

  


  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await getProfile(userId);
        const w = await getWalletOverview(userId);
        setWallet(w);
        setProfile(data);
        setTextToCopy(authUser.refer_id)
        //setProfile(authUser)
      } catch (err) {
        console.error(err);
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    }


    loadData();
  }, [userId, authUser]);

  const handleUpgrade = async () => {
    setUpgradeError("");
    setUpgradeSuccess("");

    if (!profile || !wallet) return;

    if (wallet.balance < 30) {
      setUpgradeError("You need at least €30 balance to upgrade your account.");
      return;
    }

    try {
        
      setUpgradeLoading(true);

      const data = await upgradeAccount(userId);

      setUpgradeSuccess(data.message || "Your account is now Activated.");

      // REFRESH PROFILE + WALLET
      const newProfile = await getProfile(userId);
      setProfile(newProfile.user);


      const newWallet = await getWalletOverview(userId);
      setWallet(newWallet);

      // Update global auth context user

      //login(authUser.token, newProfile.user);
      updateUser(newProfile)

    } catch (err) {
      console.error(err);
      setUpgradeError("Failed to upgrade account.");
    } finally {
      setUpgradeLoading(false);
    }
  };

    
    const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 2000); // Clear message after 2 seconds
    } catch (err) {
      setCopySuccess('Failed to copy!');
      console.error('Failed to copy text: ', err);
    }
  };

  if (loading) return <div>Loading profile...</div>;

  const display = profile || authUser || {};

  const showUpgradeButton =
  display.status === STATUS_LEVEL.NEW &&
  wallet &&
  Number(wallet.wallet.balance) >= 30;


  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fadeIn">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-600 mt-1">
            View your account details and upgrade your account.
          </p>
        </div>
        <Link to="/profile/password">
          <Button variant="outline">
            <FiKey className="inline-block mr-2 h-4 w-4" />
            Change Password
          </Button>
        </Link>
      </div>

      {/* Upgrade status messages */}
      {upgradeError && (
        <div className="card bg-gradient-to-br from-red-50 to-rose-50 border-red-200 animate-fadeIn">
          <p className="text-sm text-red-700">{upgradeError}</p>
        </div>
      )}
      {upgradeSuccess && (
        <div className="card bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-700">{upgradeSuccess}</p>
          </div>
        </div>
      )}

      <div className="card-gradient animate-scaleIn space-y-6">
        {/* Basic Info */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FiUser className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Basic Information
            </h2>
          </div>

          {error && (
            <div className="mb-4 card bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-white/60 border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <FiUser className="h-4 w-4" />
                Name
              </div>
              <div className="font-semibold text-gray-900">
                {display.name || "-"}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/60 border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <FiMapPin className="h-4 w-4" />
                Country
              </div>
              <div className="font-semibold text-gray-900">
                {display.country || "Bangladesh"}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/60 border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <FiMail className="h-4 w-4" />
                Email
              </div>
              <div className="font-mono text-sm text-gray-900">
                {display.email || "-"}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-white/60 border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <FiPhone className="h-4 w-4" />
                Phone
              </div>
              <div className="font-mono text-sm text-gray-900">
                {display.phone || "-"}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200 md:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <FiCopy className="h-4 w-4" />
                    Refer ID
                  </div>
                  <div className="font-mono text-lg font-bold text-primary-600">
                    {display.refer_id || "-"}
                  </div>
                </div>
                <Button onClick={handleCopy} variant="outline" size="sm">
                  <FiCopy className="inline-block mr-2 h-4 w-4" />
                  {copySuccess || "Copy"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className="border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <FiAward className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Account Details
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200">
              <div className="text-sm text-gray-600 mb-2">Account Status</div>
              <span
                className={`badge ${
                  display.status === "BRONZE"
                    ? "bg-amber-100 text-amber-800 border-amber-200"
                    : display.status === "ACTIVATED"
                    ? "bg-blue-100 text-blue-800 border-blue-200"
                    : "bg-gray-100 text-gray-800 border-gray-200"
                } border`}
              >
                {display.status_level || "-"}
              </span>
            </div>

            <div className="p-4 rounded-lg bg-white/60 border border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Joined At</div>
              <div className="text-sm font-medium text-gray-900">
                {display.created_at
                  ? new Date(display.created_at).toLocaleString()
                  : "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Button */}
        {showUpgradeButton && (
          <div className="pt-6 border-t">
            <Button
              variant="success"
              loading={upgradeLoading}
              onClick={handleUpgrade}
            >
              <FiAward className="inline-block mr-2 h-4 w-4" />
              Upgrade to Activated Account (€30)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;
